<script setup lang="ts">
import { InfoTip } from '@it-tools/shell'
import { useI18n } from 'vue-i18n'
import { isProviderId, type ProviderId, PROVIDERS } from '../model/catalog'
import { isProviderOption, PROVIDER_OPTIONS, type ProviderOptions } from '../model/providers'

const props = defineProps<{ provider: ProviderId; options: ProviderOptions }>()
const emit = defineEmits<{ 'update:provider': [ProviderId]; 'update:options': [ProviderOptions] }>()

const { t } = useI18n()

function pickProvider(event: Event): void {
  const target = event.target
  if (target instanceof HTMLSelectElement && isProviderId(target.value)) {
    emit('update:provider', target.value)
  }
}

function pickOption(event: Event): void {
  const target = event.target
  if (!(target instanceof HTMLSelectElement)) return
  const provider = props.provider
  if (isProviderOption(provider, target.value)) {
    emit('update:options', { ...props.options, [provider]: target.value })
  }
}
</script>

<template>
  <div class="picker">
    <div class="picker__field">
      <label for="provider">{{ t('provider.label') }}</label>
      <select id="provider" :value="provider" @change="pickProvider">
        <option v-for="id in PROVIDERS" :key="id" :value="id">
          {{ t(`provider.name.${id}`) }}
        </option>
      </select>
    </div>
    <div class="picker__field">
      <label for="provider-option" class="picker__label">
        {{ t(`provider.option.${provider}.label`) }}
        <InfoTip
          :label="t(`provider.option.${provider}.label`)"
          :text="t(`provider.option.${provider}.help`)"
        />
      </label>
      <select id="provider-option" :value="options[provider]" @change="pickOption">
        <option v-for="option in PROVIDER_OPTIONS[provider]" :key="option" :value="option">
          {{ t(`provider.option.${provider}.values.${option}`) }}
        </option>
      </select>
    </div>
  </div>
</template>

<style scoped>
.picker {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-3);
}

.picker__field {
  display: flex;
  flex: 1 1 12rem;
  flex-direction: column;
  gap: var(--space-1);
  min-width: 0;
}

label {
  font-size: var(--text-sm);
  color: var(--color-text-muted);
}

.picker__label {
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

select {
  width: 100%;
  padding: var(--space-2) var(--space-3);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface);
  color: var(--color-text);
  font: inherit;
  font-size: var(--text-sm);
  cursor: pointer;
}
</style>
